if (!customElements.get('product-compare-table')) {
  class ProductCompareTable extends HTMLElement {
    constructor() {
      super();
      this.products = [];
      this.metaFields = [];
      this.productData = {};
    }

    connectedCallback() {
      this.containerDesktop = this.querySelector('.compare-lightbox--desktop');
      this.containerMobile = this.querySelector('.compare-lightbox--mobile');
      this.loadingOverlay = this.querySelector('.loading-overlay');

      this.addEventListeners();
      this.renderTable();
    }

    addEventListeners() {
      this.addEventListener('render-product-compare-modal', this.renderTable.bind(this));
    }

    async renderTable() {
      this.classList.add('loading');
      this.loadingOverlay?.classList.add('active');

      try {
        this.products = this.getSelectedProducts();

        if (this.products.length === 0) {
          this.showEmptyState();
          return;
        }

        await this.fetchProducts();
        this.buildComparisonTable();
      } catch (error) {
        console.error('Error rendering compare table:', error);
        this.showErrorState();
      } finally {
        this.classList.remove('loading');
        this.loadingOverlay?.classList.remove('active');
      }
    }

    getSelectedProducts() {
      const compareItems = JSON.parse(localStorage.getItem('compare-products')) || [];
      return compareItems.slice(0, this.dataset.max || 4);
    }

    showEmptyState() {
      const emptyHTML = `
        <div class="empty-state">
          <p>No hay productos seleccionados para comparar.</p>
          <a href="/collections/all" class="button">Ver productos</a>
        </div>
      `;

      this.containerDesktop.innerHTML = emptyHTML;
      this.containerMobile.innerHTML = emptyHTML;
    }

    showErrorState() {
      const errorHTML = `
        <div class="error-state">
          <p>Ocurrió un error al cargar la comparación.</p>
          <button class="button retry-button">Intentar nuevamente</button>
        </div>
      `;

      this.containerDesktop.innerHTML = errorHTML;
      this.containerMobile.innerHTML = errorHTML;

      this.querySelector('.retry-button')?.addEventListener('click', () => this.renderTable());
    }

    async fetchProducts() {
      this.productData = {};

      await Promise.all(
        this.products.map(async (product, index) => {
          try {
            const response = await fetch(`${product.url}?view=compare`);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const text = await response.text();
            const parser = new DOMParser();
            const htmlDoc = parser.parseFromString(text, 'text/html');

            const imageHTML = htmlDoc.querySelector('.block-product-cart')?.outerHTML || '-';
            const name = htmlDoc.querySelector('.block-product-info')?.innerHTML || '-';
            const isPrimary = index === 0;

            // Inyecta el atributo data-is-primary
            const imageWithFlag = imageHTML.replace(
              '<product-card',
              `<product-card data-is-primary="${isPrimary}"`
            );

            this.productData[product.id] = {
              image: imageWithFlag,
              name: imageWithFlag,
              brand: htmlDoc.querySelector('.block-brand')?.innerHTML || '-',
              price: htmlDoc.querySelector('.block-price')?.innerHTML || '-',
              colors: htmlDoc.querySelector('.block-colors')?.innerHTML || '-',
              availability: htmlDoc.querySelector('.block-availability')?.innerHTML || '-',
              metaFields: this.extractMetaFields(htmlDoc.querySelector('.block-metafields')),
              isPrimary
            };
          } catch (error) {
            console.error(`Error fetching product ${product.id}:`, error);
            this.productData[product.id] = this.createErrorData();
          }
        })
      );
    }

    extractMetaFields(container) {
      if (!container) return {};

      const metaFields = {};
      const elements = container.querySelectorAll('.compare-attribute');

      elements.forEach(el => {
        const label = el.dataset.label;
        metaFields[label] = el.innerHTML;

        if (!this.metaFields.includes(label)) {
          this.metaFields.push(label);
        }
      });
      return metaFields;
    }

    createErrorData() {
      return {
        image: '<span class="error">Error</span>',
        name: '<span class="error">Error</span>',
        brand: '<span class="error">Error</span>',
        price: '<span class="error">Error</span>',
        colors: '<span class="error">Error</span>',
        availability: '<span class="error">Error</span>',
        metaFields: {}
      };
    }

    buildComparisonTable() {
      this.containerDesktop.innerHTML = '';
      this.containerMobile.innerHTML = '';

      this.addDesktopRow('', 'image', 'product-image');
      this.addDesktopRow('Marca', 'brand');
      this.addDesktopRow('Precio', 'price');
      this.addDesktopRow('Color', 'colors');
      this.addDesktopRow('Disponibilidad', 'availability');

      this.metaFields.forEach(field => {
        this.addDesktopRow(
          field.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          field,
          'feature',
          true
        );
      });

      this.buildMobileComparison();
    }

    addDesktopRow(title, property, rowClass = 'feature', isMetaField = false) {
      const rowDiv = document.createElement('div');
      rowDiv.classList.add('comparison-row', rowClass);

      const titleCell = document.createElement('div');
      titleCell.classList.add('cell');

      if (rowClass === 'product-image') {
        titleCell.classList.add('blank', 'title-header');
      } else {
        titleCell.classList.add('feature-title');
      }

      titleCell.innerHTML = title;
      rowDiv.appendChild(titleCell);

      this.products.forEach(product => {
        const data = this.productData[product.id];
        const value = isMetaField ? data.metaFields[property] || '-' : data[property] || '-';

        const valueCell = document.createElement('div');
        valueCell.classList.add('cell');
        if (rowClass !== 'product-image') {
          valueCell.classList.add('feature-detail');
        }
        valueCell.innerHTML = value;
        rowDiv.appendChild(valueCell);
      });

      this.containerDesktop.appendChild(rowDiv);
    }

    buildMobileComparison() {
      const stickyHeader = document.createElement('div');
      stickyHeader.className = 'comparison-content-ctn sticky-header mobile';

      const featuresRow = document.createElement('div');
      featuresRow.className = 'comparison-row product-features';

      const blankCell = document.createElement('div');
      blankCell.className = 'cell blank';
      featuresRow.appendChild(blankCell);

      const priceTitleCell = document.createElement('div');
      priceTitleCell.className = 'cell price-title';
      featuresRow.appendChild(priceTitleCell);

      ['Marca', 'Precio', 'Color', 'Disponibilidad'].forEach(feature => {
        const featureCell = document.createElement('div');
        featureCell.className = 'cell feature-title';
        featureCell.textContent = feature;
        featuresRow.appendChild(featureCell);
      });

      this.metaFields.forEach(field => {
        const featureCell = document.createElement('div');
        featureCell.className = 'cell feature-title';
        featureCell.textContent = field.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        featuresRow.appendChild(featureCell);
      });

      stickyHeader.appendChild(featuresRow);
      this.containerMobile.appendChild(stickyHeader);

      const contentContainer = document.createElement('div');
      contentContainer.className = 'comparison-content-ctn compare-content mobile';

      this.products.forEach(product => {
        const data = this.productData[product.id];
        const productRow = document.createElement('div');
        productRow.className = 'comparison-row product-details';

        const imageCell = document.createElement('div');
        imageCell.className = 'cell sticky-left';
        imageCell.innerHTML = data.image || '-';
        productRow.appendChild(imageCell);

        const nameCell = document.createElement('div');
        nameCell.className = 'cell price-title';
        nameCell.innerHTML = data.image || '-';
        productRow.appendChild(nameCell);

        [data.brand, data.price, data.colors, data.availability].forEach(value => {
          const detailCell = document.createElement('div');
          detailCell.className = 'cell feature-detail';
          detailCell.innerHTML = value || '-';
          productRow.appendChild(detailCell);
        });

        this.metaFields.forEach(field => {
          const metaCell = document.createElement('div');
          metaCell.className = 'cell feature-detail';
          metaCell.innerHTML = data.metaFields[field] || '-';
          productRow.appendChild(metaCell);
        });

        contentContainer.appendChild(productRow);
      });

      this.containerMobile.appendChild(contentContainer);
    }
  }

  customElements.define('product-compare-table', ProductCompareTable);
}
